import React, { useEffect, useState, useRef, lazy, Component } from 'react';
import { useChat } from '../contexts/ChatContext';
import { useAuth } from '../contexts/AuthContext';
import { useOrders } from '../contexts/OrderContext';
import { Send, MessageCircle, ArrowLeft, CheckCheck, Paperclip, X, FileText, Download, Loader2, CheckCircle2, Tag, Package, ChevronRight, ShoppingBag, Clock, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { displayProductName } from '../utils/formatters';
const FILE_SIZE_LIMIT = 5 * 1024 * 1024;
const ACCEPTED_TYPES = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv';
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
function isImageType(type?: string): boolean {
  return !!type && type.startsWith('image/');
}
function FileAttachment({
  msg,
  isMe
}: {
  msg: any;
  isMe: boolean;
}) {
  if (!msg.fileUrl) return null;
  if (isImageType(msg.fileType)) {
    return <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer" className="block mt-2" data-id="element-2691">
        <img src={msg.fileUrl} alt={msg.fileName || 'Imagen'} className="max-w-[280px] max-h-[200px] rounded-xl object-cover border border-gray-200/50" loading="lazy" data-id="element-2692" />
      </a>;
  }
  return <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-3 mt-2 p-3 rounded-xl border transition-colors ${isMe ? 'bg-white/10 border-white/20 hover:bg-white/15' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`} data-id="element-2693">
      <div className={`p-2 rounded-lg ${isMe ? 'bg-white/15' : 'bg-gray-200'}`} data-id="element-2694">
        <FileText size={18} className={isMe ? 'text-white' : 'text-gray-600'} data-id="element-2695" />
      </div>
      <div className="flex-1 min-w-0" data-id="element-2696">
        <p className={`text-xs font-medium truncate ${isMe ? 'text-white' : 'text-gray-900'}`} data-id="element-2697">
          {msg.fileName || 'Archivo'}
        </p>
        {msg.fileSize && <p className={`text-[10px] ${isMe ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-2698">
            {formatFileSize(msg.fileSize)}
          </p>}
      </div>
      <Download size={14} className={isMe ? 'text-gray-300' : 'text-gray-400'} data-id="element-2699" />
    </a>;
}
// Componente para mostrar un pedido adjunto en un mensaje
function OrderAttachment({
  text
}: {
  text: string;
}) {
  // Detectar si el mensaje contiene info de pedido con formato especial
  const orderMatch = text.match(/📦 Pedido #(\d+)/);
  if (!orderMatch) return null;
  const lines = text.split('\n').filter(l => l.trim());
  const orderNo = orderMatch[1];
  const productLine = lines.find(l => l.startsWith('🍽️'));
  const dateLine = lines.find(l => l.startsWith('📅'));
  const statusLine = lines.find(l => l.startsWith('Estado:'));
  const commentLine = lines.find(l => l.startsWith('💬'));
  return <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-2" data-id="element-2700">
      <div className="flex items-center gap-2 mb-2" data-id="element-2701">
        <div className="p-1.5 bg-amber-100 rounded-lg" data-id="element-2702">
          <Package size={14} className="text-amber-700" data-id="element-2703" />
        </div>
        <span className="text-xs font-bold text-amber-800" data-id="element-2704">
          Pedido #{orderNo}
        </span>
      </div>
      {productLine && <p className="text-xs text-amber-700 mb-1" data-id="element-2705">{productLine}</p>}
      {dateLine && <p className="text-[10px] text-amber-600" data-id="element-2706">{dateLine}</p>}
      {statusLine && <p className="text-[10px] text-amber-600" data-id="element-2707">{statusLine}</p>}
      {commentLine && <p className="text-xs text-amber-800 mt-2 pt-2 border-t border-amber-200" data-id="element-2708">
          {commentLine}
        </p>}
    </div>;
}
const TICKET_SUGGESTIONS = ['Problema con mi pedido', 'Consulta sobre el menú', 'Error en mi cuenta', 'Solicitud de cambio', 'Otro'];
// Tipos de problemas con pedidos
const ORDER_ISSUE_TYPES = [{
  label: 'Pedido incorrecto',
  emoji: '❌'
}, {
  label: 'Pedido incompleto',
  emoji: '📦'
}, {
  label: 'Demora en entrega',
  emoji: '⏰'
}, {
  label: 'Calidad del producto',
  emoji: '⚠️'
}, {
  label: 'Otro problema',
  emoji: '💬'
}];
export function EmployeeChatPage() {
  useDocumentTitle('Chat de Soporte');
  const {
    user
  } = useAuth();
  const {
    messages,
    sendMessage,
    markAsRead,
    uploading,
    conversations,
    setTyping,
    typingIn,
    reopenConversation
  } = useChat();
  const {
    orders
  } = useOrders();
  const navigate = useNavigate();
  const [newMessage, setNewMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [ticketTitle, setTicketTitle] = useState('');
  const [showTicketForm, setShowTicketForm] = useState(false);
  // Estados para selección de pedido
  const [showOrderPicker, setShowOrderPicker] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [orderIssueType, setOrderIssueType] = useState('');
  const [orderComment, setOrderComment] = useState('');
  // Estado para adjuntar pedido en chat activo
  const [showOrderAttach, setShowOrderAttach] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasScrolledInitially = useRef(false);
  const justSentMessage = useRef(false);
  const myMessages = messages.filter(m => m.conversationId === user?.id);
  const messageCount = myMessages.length;
  const hasExistingChat = messageCount > 0;
  const myConversation = conversations.find(c => c.id === user?.id);
  const isClosed = myConversation?.status === 'closed';
  // Filtrar pedidos del empleado actual (últimos 15 días)
  const myOrders = orders.filter(o => {
    const isMyOrder = o.employeeId === user?.id || o.name === user?.name;
    if (!isMyOrder) return false;
    // Solo pedidos de los últimos 15 días
    const orderDate = new Date(o.orderDateISO || o.orderDate);
    const fifteenDaysAgo = new Date();
    fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
    return orderDate >= fifteenDaysAgo;
  }).sort((a, b) => {
    const dateA = new Date(a.orderDateISO || a.orderDate);
    const dateB = new Date(b.orderDateISO || b.orderDate);
    return dateB.getTime() - dateA.getTime();
  }).slice(0, 10); // Máximo 10 pedidos recientes
  // Show ticket form if no existing chat
  useEffect(() => {
    if (!hasExistingChat && user) {
      setShowTicketForm(true);
    } else {
      setShowTicketForm(false);
    }
  }, [hasExistingChat, user]);
  useEffect(() => {
    if (user) markAsRead(user.id);
  }, [user?.id]);
  useEffect(() => {
    if (messageCount > 0 && !hasScrolledInitially.current) {
      hasScrolledInitially.current = true;
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: 'auto'
        });
      }, 50);
    }
  }, [messageCount]);
  useEffect(() => {
    if (justSentMessage.current) {
      justSentMessage.current = false;
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 150);
    }
  }, [messageCount]);
  const handleCreateTicket = () => {
    if (!ticketTitle.trim()) return;
    // If conversation was closed, reopen it first
    if (isClosed && user) {
      reopenConversation(user.id);
    }
    justSentMessage.current = true;
    sendMessage(`🎫 ${ticketTitle.trim()}`, undefined);
    setShowTicketForm(false);
    setTicketTitle('');
    setTimeout(() => inputRef.current?.focus(), 200);
  };
  // Manejar selección de sugerencia de ticket
  const handleTicketSuggestion = (suggestion: string) => {
    if (suggestion === 'Problema con mi pedido') {
      setShowOrderPicker(true);
      setSelectedOrder(null);
      setOrderIssueType('');
      setOrderComment('');
    } else {
      setTicketTitle(suggestion);
      setShowOrderPicker(false);
    }
  };
  // Enviar reporte de pedido como ticket inicial
  const handleSendOrderReport = () => {
    if (!selectedOrder || !orderIssueType) return;
    // If conversation was closed, reopen it first
    if (isClosed && user) {
      reopenConversation(user.id);
    }
    justSentMessage.current = true;
    const productName = displayProductName(selectedOrder.product);
    let message = `🎫 Problema con mi pedido\n\n`;
    message += `📦 Pedido #${selectedOrder.orderNo}\n`;
    message += `🍽️ ${productName}\n`;
    message += `📅 ${selectedOrder.orderDate}\n`;
    message += `Estado: ${selectedOrder.status}\n`;
    message += `\n${orderIssueType}`;
    if (orderComment.trim()) {
      message += `\n💬 ${orderComment.trim()}`;
    }
    sendMessage(message, undefined);
    setShowTicketForm(false);
    setShowOrderPicker(false);
    setSelectedOrder(null);
    setOrderIssueType('');
    setOrderComment('');
    setTimeout(() => inputRef.current?.focus(), 200);
  };
  // Adjuntar pedido en chat activo
  const handleAttachOrder = (order: any) => {
    const productName = displayProductName(order.product);
    let message = `📦 Pedido #${order.orderNo}\n`;
    message += `🍽️ ${productName}\n`;
    message += `📅 ${order.orderDate}\n`;
    message += `Estado: ${order.status}`;
    justSentMessage.current = true;
    sendMessage(message, undefined);
    setShowOrderAttach(false);
  };
  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedFile) return;
    justSentMessage.current = true;
    sendMessage(newMessage, undefined, selectedFile || undefined);
    setNewMessage('');
    clearFile();
    inputRef.current?.focus();
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > FILE_SIZE_LIMIT) {
      alert('El archivo es muy grande. Máximo 5MB.');
      return;
    }
    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setFilePreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };
  const clearFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  // Group messages by date
  const groupedMessages: {
    date: string;
    messages: typeof myMessages;
  }[] = [];
  let currentDate = '';
  myMessages.forEach(msg => {
    const msgDate = new Date(msg.timestamp).toLocaleDateString('es-DO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    if (msgDate !== currentDate) {
      currentDate = msgDate;
      groupedMessages.push({
        date: msgDate,
        messages: []
      });
    }
    groupedMessages[groupedMessages.length - 1].messages.push(msg);
  });
  // Helper para color de estado
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pendiente':
        return 'bg-yellow-100 text-yellow-800';
      case 'En Preparación':
        return 'bg-blue-100 text-blue-800';
      case 'Listo':
        return 'bg-green-100 text-green-800';
      case 'Entregado':
        return 'bg-gray-100 text-gray-600';
      case 'Cancelado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };
  return <div className="fixed inset-0 flex flex-col bg-white z-10" style={{
    height: '100dvh'
  }} data-id="element-2709">
      {/* Fixed Header */}
      <div className="shrink-0 sticky top-0 border-b border-gray-200 bg-white px-4 sm:px-6 py-3 z-10" data-id="element-2710">
        <div className="flex items-center gap-4 max-w-4xl mx-auto" data-id="element-2711">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-600" data-id="element-2712">
            <ArrowLeft size={20} data-id="element-2713" />
          </button>
          <div className="flex items-center gap-3" data-id="element-2714">
            <div className="w-11 h-11 rounded-full bg-gray-900 flex items-center justify-center text-white" data-id="element-2715">
              <MessageCircle size={20} data-id="element-2716" />
            </div>
            <div data-id="element-2717">
              <h1 className="font-bold text-gray-900 text-lg" data-id="element-2718">
                Soporte Cricket
              </h1>
              <div className="flex items-center gap-1.5" data-id="element-2719">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-2720"></span>
                <span className="text-xs text-gray-500" data-id="element-2721">En línea</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 bg-gray-50/50 min-h-0" data-id="element-2722">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-2723">
          {/* Ticket Creation Form (shown when no messages) */}
          {showTicketForm ? showOrderPicker /* ── Selector de Pedido ── */ ? <div className="px-2 py-2" data-id="element-2724">
                <button onClick={() => {
            setShowOrderPicker(false);
            setSelectedOrder(null);
            setOrderIssueType('');
            setOrderComment('');
          }} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors" data-id="element-2725">
                  <ArrowLeft size={16} data-id="element-2726" />
                  Volver
                </button>

                {!selectedOrder /* Paso 1: Seleccionar pedido */ ? <div data-id="element-2727">
                    <div className="flex items-center gap-3 mb-4" data-id="element-2728">
                      <div className="p-2 bg-amber-100 rounded-xl" data-id="element-2729">
                        <ShoppingBag size={20} className="text-amber-700" data-id="element-2730" />
                      </div>
                      <div data-id="element-2731">
                        <h3 className="font-bold text-gray-900" data-id="element-2732">
                          Selecciona tu pedido
                        </h3>
                        <p className="text-xs text-gray-500" data-id="element-2733">
                          Elige el pedido que deseas reportar
                        </p>
                      </div>
                    </div>

                    {myOrders.length === 0 ? <div className="text-center py-8 bg-white rounded-xl border border-gray-200" data-id="element-2734">
                        <Package size={32} className="mx-auto text-gray-300 mb-3" data-id="element-2735" />
                        <p className="text-sm text-gray-500 font-medium" data-id="element-2736">
                          No tienes pedidos recientes
                        </p>
                        <p className="text-xs text-gray-400 mt-1" data-id="element-2737">
                          Los pedidos de los últimos 15 días aparecerán aquí
                        </p>
                        <button onClick={() => {
                setShowOrderPicker(false);
                setTicketTitle('Problema con mi pedido');
              }} className="mt-4 text-xs text-gray-600 underline hover:text-gray-800" data-id="element-2738">
                          Reportar sin seleccionar pedido
                        </button>
                      </div> : <div className="space-y-2" data-id="element-2739">
                        {myOrders.map(order => <button key={order.id} onClick={() => setSelectedOrder(order)} className="w-full bg-white p-3.5 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left flex items-center gap-3 group" data-id="element-2740">
                            <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors shrink-0" data-id="element-2741">
                              <Package size={18} className="text-gray-600" data-id="element-2742" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-2743">
                              <div className="flex items-center gap-2 mb-0.5" data-id="element-2744">
                                <span className="text-xs font-mono font-bold text-gray-500" data-id="element-2745">
                                  #{order.orderNo}
                                </span>
                                <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${getStatusColor(order.status)}`} data-id="element-2746">
                                  {order.status}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-gray-900 truncate" data-id="element-2747">
                                {displayProductName(order.product)}
                              </p>
                              <p className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5" data-id="element-2748">
                                <Clock size={10} data-id="element-2749" />
                                {order.orderDate}
                              </p>
                            </div>
                            <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500 shrink-0 transition-colors" data-id="element-2750" />
                          </button>)}
                      </div>}
                  </div> /* Paso 2: Describir el problema */ : <div data-id="element-2751">
                    {/* Pedido seleccionado */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 mb-5" data-id="element-2752">
                      <div className="flex items-center justify-between mb-2" data-id="element-2753">
                        <div className="flex items-center gap-2" data-id="element-2754">
                          <Package size={16} className="text-amber-700" data-id="element-2755" />
                          <span className="text-sm font-bold text-amber-800" data-id="element-2756">
                            Pedido #{selectedOrder.orderNo}
                          </span>
                        </div>
                        <button onClick={() => setSelectedOrder(null)} className="text-[10px] text-amber-600 hover:text-amber-800 font-medium px-2 py-0.5 rounded-lg hover:bg-amber-100 transition-colors" data-id="element-2757">
                          Cambiar
                        </button>
                      </div>
                      <p className="text-xs text-amber-700" data-id="element-2758">
                        {displayProductName(selectedOrder.product)}
                      </p>
                      <p className="text-[10px] text-amber-600 mt-0.5" data-id="element-2759">
                        {selectedOrder.orderDate}
                      </p>
                    </div>

                    {/* Tipo de problema */}
                    <div className="mb-4" data-id="element-2760">
                      <p className="text-xs font-medium text-gray-500 mb-2.5" data-id="element-2761">
                        ¿Cuál es el problema?
                      </p>
                      <div className="grid grid-cols-2 gap-2" data-id="element-2762">
                        {ORDER_ISSUE_TYPES.map(issue => <button key={issue.label} onClick={() => setOrderIssueType(`${issue.emoji} ${issue.label}`)} className={`p-3 rounded-xl border text-left text-xs font-medium transition-all ${orderIssueType === `${issue.emoji} ${issue.label}` ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2763">
                            <span className="text-base block mb-1" data-id="element-2764">
                              {issue.emoji}
                            </span>
                            {issue.label}
                          </button>)}
                      </div>
                    </div>

                    {/* Comentario adicional */}
                    <div className="mb-5" data-id="element-2765">
                      <p className="text-xs font-medium text-gray-500 mb-1.5" data-id="element-2766">
                        Detalles adicionales (opcional)
                      </p>
                      <textarea value={orderComment} onChange={e => setOrderComment(e.target.value)} placeholder="Describe el problema con más detalle..." rows={2} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all resize-none" data-id="element-2767" />
                    </div>

                    {/* Botón enviar */}
                    <button onClick={handleSendOrderReport} disabled={!orderIssueType} className="w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2" data-id="element-2768">
                      <Send size={16} data-id="element-2769" />
                      Reportar pedido
                    </button>
                  </div>}
              </div> /* ── Formulario de ticket original ── */ : <div className="flex flex-col items-center justify-center text-center px-4 py-6" data-id="element-2770">
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3" data-id="element-2771">
                  <Tag size={24} className="text-gray-400" data-id="element-2772" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-2773">
                  ¡Hola, {user?.name?.split(' ')[0]}!
                </h3>
                <p className="text-sm text-gray-500 max-w-sm mb-4" data-id="element-2774">
                  ¿En qué podemos ayudarte? Escribe un título para tu consulta.
                </p>

                <div className="w-full max-w-sm space-y-4" data-id="element-2775">
                  <input type="text" value={ticketTitle} onChange={e => setTicketTitle(e.target.value)} placeholder="Ej: Problema con mi pedido de hoy" className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all" onKeyDown={e => {
              if (e.key === 'Enter') handleCreateTicket();
            }} autoFocus data-id="element-2776" />

                  <div className="flex flex-wrap gap-2 justify-center" data-id="element-2777">
                    {TICKET_SUGGESTIONS.map(s => <button key={s} onClick={() => handleTicketSuggestion(s)} className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${s === 'Problema con mi pedido' ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 flex items-center gap-1' : ticketTitle === s ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`} data-id="element-2778">
                        {s === 'Problema con mi pedido' && <Package size={12} data-id="element-2779" />}
                        {s}
                      </button>)}
                  </div>

                  <button onClick={handleCreateTicket} disabled={!ticketTitle.trim()} className="w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2" data-id="element-2780">
                    <Send size={16} data-id="element-2781" />
                    Iniciar conversación
                  </button>
                </div>
              </div> : <>
              {groupedMessages.map((group, gi) => <div key={gi} data-id="element-2782">
                  <div className="flex items-center justify-center mb-4" data-id="element-2783">
                    <span className="text-[10px] font-medium text-gray-400 bg-white px-3 py-1 rounded-full border border-gray-100 shadow-sm capitalize" data-id="element-2784">
                      {group.date}
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-2785">
                    {group.messages.map((msg, mi) => {
                const isMe = msg.senderRole === 'employee';
                const showName = !isMe && (mi === 0 || group.messages[mi - 1]?.senderRole !== msg.senderRole);
                const hasOrderInfo = msg.text?.includes('📦 Pedido #');
                return <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-id="element-2786">
                          <div className="max-w-[80%] sm:max-w-[70%]" data-id="element-2787">
                            {showName && <div className="mb-1 ml-1" data-id="element-2788">
                                <p className="text-[11px] font-semibold text-gray-500" data-id="element-2789">
                                  {msg.senderName}
                                </p>
                                <p className="text-[10px] text-blue-500 font-medium" data-id="element-2790">
                                  Administrador
                                </p>
                              </div>}
                            <div className={`px-4 py-3 text-sm leading-relaxed shadow-sm ${isMe ? 'bg-gray-900 text-white rounded-2xl rounded-tr-md' : 'bg-white text-gray-800 border border-gray-100 rounded-2xl rounded-tl-md'}`} data-id="element-2791">
                              {hasOrderInfo && !isMe ? <>
                                  <OrderAttachment text={msg.text} data-id="element-2792" />
                                  {/* Mostrar texto que no sea parte del pedido */}
                                  {msg.text.split('\n').filter((l: string) => l.startsWith('🎫')).map((line: string, i: number) => <p key={i} data-id="element-2793">{line}</p>)}
                                </> : msg.text && <p className="whitespace-pre-line" data-id="element-2794">
                                    {msg.text}
                                  </p>}
                              <FileAttachment msg={msg} isMe={isMe} data-id="element-2795" />
                              <div className={`flex items-center gap-1 mt-1.5 ${isMe ? 'justify-end' : ''}`} data-id="element-2796">
                                <span className="text-[10px] text-gray-400" data-id="element-2797">
                                  {new Date(msg.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                                </span>
                                {isMe && msg.read && <CheckCheck size={12} className="text-emerald-400" data-id="element-2798" />}
                              </div>
                            </div>
                          </div>
                        </div>;
              })}
                  </div>
                </div>)}

              {isClosed && <div className="flex justify-center py-4" data-id="element-2799">
                  <div className="bg-gray-100 text-gray-500 text-xs px-4 py-2 rounded-full flex items-center gap-2 border border-gray-200" data-id="element-2800">
                    <CheckCircle2 size={14} data-id="element-2801" />
                    Tu consulta fue marcada como resuelta
                  </div>
                </div>}

              {/* Typing indicator */}
              {!isClosed && user && typingIn.has(user.id) && <div className="flex justify-start" data-id="element-2802">
                  <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-md px-4 py-3 shadow-sm" data-id="element-2803">
                    <div className="flex items-center gap-1" data-id="element-2804">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-2805"></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-2806"></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-2807"></span>
                    </div>
                  </div>
                </div>}
            </>}
          <div ref={messagesEndRef} data-id="element-2808" />
        </div>
      </div>

      {/* Order Attach Picker (overlay en chat activo) */}
      {showOrderAttach && <div className="absolute inset-0 z-20 bg-white flex flex-col" data-id="element-2809">
          <div className="shrink-0 border-b border-gray-200 px-4 py-3 flex items-center gap-3" data-id="element-2810">
            <button onClick={() => setShowOrderAttach(false)} className="p-2 -ml-2 rounded-xl hover:bg-gray-100 text-gray-600 transition-colors" data-id="element-2811">
              <ArrowLeft size={20} data-id="element-2812" />
            </button>
            <div data-id="element-2813">
              <h3 className="font-bold text-gray-900 text-sm" data-id="element-2814">
                Adjuntar pedido
              </h3>
              <p className="text-[10px] text-gray-500" data-id="element-2815">
                Selecciona un pedido para enviarlo en el chat
              </p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2" data-id="element-2816">
            {myOrders.length === 0 ? <div className="text-center py-12" data-id="element-2817">
                <Package size={32} className="mx-auto text-gray-300 mb-3" data-id="element-2818" />
                <p className="text-sm text-gray-500" data-id="element-2819">
                  No tienes pedidos recientes
                </p>
              </div> : myOrders.map(order => <button key={order.id} onClick={() => handleAttachOrder(order)} className="w-full bg-white p-3.5 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all text-left flex items-center gap-3" data-id="element-2820">
                  <div className="p-2 bg-gray-100 rounded-lg shrink-0" data-id="element-2821">
                    <Package size={18} className="text-gray-600" data-id="element-2822" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2823">
                    <div className="flex items-center gap-2 mb-0.5" data-id="element-2824">
                      <span className="text-xs font-mono font-bold text-gray-500" data-id="element-2825">
                        #{order.orderNo}
                      </span>
                      <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${getStatusColor(order.status)}`} data-id="element-2826">
                        {order.status}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-2827">
                      {displayProductName(order.product)}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-2828">
                      {order.orderDate}
                    </p>
                  </div>
                  <Send size={14} className="text-gray-400 shrink-0" data-id="element-2829" />
                </button>)}
          </div>
        </div>}

      {/* File Preview */}
      {selectedFile && <div className="px-4 pt-3 bg-white border-t border-gray-100 shrink-0" data-id="element-2830">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 max-w-4xl mx-auto" data-id="element-2831">
            {filePreview ? <img src={filePreview} alt="" className="w-12 h-12 rounded-lg object-cover" data-id="element-2832" /> : <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center" data-id="element-2833">
                <FileText size={20} className="text-gray-500" data-id="element-2834" />
              </div>}
            <div className="flex-1 min-w-0" data-id="element-2835">
              <p className="text-sm font-medium text-gray-900 truncate" data-id="element-2836">
                {selectedFile.name}
              </p>
              <p className="text-xs text-gray-500" data-id="element-2837">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
            <button onClick={clearFile} className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2838">
              <X size={16} data-id="element-2839" />
            </button>
          </div>
        </div>}

      {/* Input - only show when chat is active and not closed */}
      {!showTicketForm && !isClosed && <div className="shrink-0 p-4 bg-white border-t border-gray-100" data-id="element-2840">
          <form onSubmit={handleSend} className="flex items-center gap-2 max-w-4xl mx-auto" data-id="element-2841">
            <input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES} onChange={handleFileSelect} className="hidden" data-id="element-2842" />
            <button type="button" onClick={() => fileInputRef.current?.click()} className="p-3 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0" title="Adjuntar archivo (máx. 5MB)" data-id="element-2843">
              <Paperclip size={20} data-id="element-2844" />
            </button>

            {/* Botón adjuntar pedido */}
            <button type="button" onClick={() => setShowOrderAttach(true)} className="p-3 rounded-xl text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors shrink-0" title="Adjuntar pedido" data-id="element-2845">
              <Package size={20} data-id="element-2846" />
            </button>

            <div className="relative flex-1" data-id="element-2847">
              <input ref={inputRef} type="text" value={newMessage} onChange={e => {
            setNewMessage(e.target.value);
            if (user) setTyping(user.id);
          }} placeholder="Escribe un mensaje..." className="w-full pl-5 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all" data-id="element-2848" />
            </div>
            <button type="submit" disabled={!newMessage.trim() && !selectedFile || uploading} className="p-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95 shrink-0" data-id="element-2849">
              {uploading ? <Loader2 size={18} className="animate-spin" data-id="element-2850" /> : <Send size={18} data-id="element-2851" />}
            </button>
          </form>
        </div>}

      {/* Closed chat notice */}
      {!showTicketForm && isClosed && <div className="shrink-0 p-4 bg-gray-50 border-t border-gray-200" data-id="element-2852">
          <div className="flex flex-col items-center gap-3 max-w-4xl mx-auto" data-id="element-2853">
            <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-2854">
              <CheckCircle2 size={16} className="text-gray-400" data-id="element-2855" />
              <span data-id="element-2856">Esta conversación fue cerrada por el administrador</span>
            </div>
            <button onClick={() => {
          setShowTicketForm(true);
          setTicketTitle('');
          setShowOrderPicker(false);
          setSelectedOrder(null);
          setOrderIssueType('');
          setOrderComment('');
        }} className="px-5 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-all active:scale-95 flex items-center gap-2" data-id="element-2857">
              <MessageCircle size={16} data-id="element-2858" />
              Nueva consulta
            </button>
          </div>
        </div>}
    </div>;
}